import {
  Content as RadixContent,
  Portal as RadixPortal,
} from '@radix-ui/react-dropdown-menu';
import {
  forwardRef,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import { useMenuContext } from './hooks/useMenuContext.js';
import * as styles from './Menu.sty.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { WithChildren } from '../../core/types/with-children.js';

/**
 * @public
 */
export interface MenuContentProps
  extends WithChildren, MaskingProps, DataTestId {
  /**
   * The preferred side where the menu content should be rendered relative to the
   * trigger element. This value may be ignored if collisions occur.
   * This prop is ignored within submenus.
   *
   * @defaultValue 'bottom'
   */
  side?: 'bottom' | 'top' | 'right' | 'left';
  /**
   * The horizontal position where the menu content should be rendered relative
   * to the trigger element. This value may be ignored if collisions occur
   * or when `side` is set to `'right'` or `'left'`.
   * This prop is ignored within submenus.
   *
   * @defaultValue 'start'
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * Event handler called when focus moves to the trigger after closing.
   * It can be prevented by calling event.preventDefault.
   */
  onCloseAutoFocus?: (event: Event) => void;
  /**
   * When true, overrides the side and align preferences to prevent collisions
   * with boundary edges.
   *
   * @defaultValue true
   */
  avoidCollisions?: boolean;
  /**
   * Additional elements can be provided to be included in the boundary check for menu.
   * By default this is the viewport.
   */
  collisionBoundary?: Element | null | Array<Element | null>;
}

const SIDE_OFFSET = 4;
type InternalMenuContentProps = MenuContentProps & {
  /** @internal */
  _onTabKeyPressed: () => void;
  /** @internal */
  _sideOffset?: number;
};

/**
 * The component that pops out when the dropdown menu is open.
 * @public
 */
export const Content = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuContentProps
>((props, forwardedRef) => {
  const {
    side = 'bottom',
    alignment = 'start',
    onCloseAutoFocus,
    avoidCollisions = true,
    _sideOffset = SIDE_OFFSET,
    _onTabKeyPressed,
    children,
    collisionBoundary,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props as InternalMenuContentProps;
  const tabKeyHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (_onTabKeyPressed && e.key === 'Tab') {
        _onTabKeyPressed();
      }
    },
    [_onTabKeyPressed],
  );
  const { style } = useOverlayStacking(undefined, 'overlay');
  const { setIsOpen } = useMenuContext();

  const handleOnKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      // Only close menu on escape when it is nested in other overlays (modal, sheet, ...).
      if (e.key === 'Escape') {
        setIsOpen(false);
        e.stopPropagation();
      }
    },
    [setIsOpen],
  );

  return (
    <RadixPortal>
      <RadixContent
        {...remainingProps}
        data-testid={dataTestId}
        ref={forwardedRef}
        side={side}
        align={alignment}
        onKeyUp={tabKeyHandler}
        onCloseAutoFocus={onCloseAutoFocus}
        sideOffset={_sideOffset}
        avoidCollisions={avoidCollisions}
        className={styles.content}
        style={{ zIndex: style.zIndex }}
        onKeyDown={handleOnKeyDown}
        collisionBoundary={collisionBoundary}
        // stop event propagation so that selecting a menu item does not close dismissible overlays if the menu is placed inside one
        onPointerDownCapture={(e: ReactMouseEvent) => e.stopPropagation()}
        onPointerUpCapture={(e: ReactMouseEvent) => e.stopPropagation()}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        {children}
      </RadixContent>
    </RadixPortal>
  );
});

(Content as typeof Content & { displayName: string }).displayName =
  'Menu.Content';
